import type { TranscriptResponse } from '@/lib/SessionRecordingProvider/t11nClient';
import { store } from '@/store';
import { boundedErrorName } from '@/utils/logger/bounded-error-name';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { sumBy } from 'lodash-es';
import { useCallback } from 'react';
import { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY } from '../query/useQueryT11nTranscriptBySessionId';
import { resolveStopDurationSeconds } from './resolveStopDurationSeconds';
import { useMutateUpdateSessionDuration } from './useMutateUpdateSessionDuration';

/**
 * Single owner of the "persist the authoritative session duration on stop"
 * step shared by the desktop stop flows (APP-7623): reads the duration
 * sources (UI timer, cached session, uploaded t11n chunks, optional Rust
 * segment wall-clock), resolves the max via `resolveStopDurationSeconds`,
 * skips 0, and PATCHes.
 *
 * The returned function is awaitable so callers can order the PATCH before
 * dispatching note generation (the SCR-2711 backend gate reads the *server*
 * duration) — and it never rejects: a failed PATCH is logged and must not
 * break the stop flow.
 */
export function usePersistAuthoritativeDuration({ sessionId }: { sessionId: string }) {
  const queryClient = useQueryClient();
  const { mutateAsync: updateSessionDuration } = useMutateUpdateSessionDuration();

  return useCallback(
    async (rustDurationMs?: number): Promise<void> => {
      const t11nData = queryClient.getQueryData<TranscriptResponse>([
        TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY,
        sessionId,
      ]);
      // v2 chunk durations are milliseconds (mirrors useUpdateTimerFromSession).
      const t11nDurationSeconds = Math.round(
        sumBy(
          t11nData?.recordings.flatMap((recording) => recording.chunks) ?? [],
          (chunk) => chunk.audio_info.duration ?? 0
        ) / 1000
      );

      const duration = resolveStopDurationSeconds({
        timerSeconds: Number(store.getState().timerModel.seconds),
        cachedDurationSeconds: queryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId },
        ])?.duration,
        t11nDurationSeconds,
        rustDurationMs,
      });
      if (duration <= 0) return;

      logger.info('[StopRecording] Persisting authoritative session duration', {
        sessionId,
        duration,
        rustDurationMs,
      });
      await updateSessionDuration({ sessionId, duration }).catch((error: unknown) => {
        // Eventual persistence only — retries already happened inside the
        // mutation; the stop flow (and note generation) must proceed regardless.
        logger.error('[StopRecording] Failed to persist session duration', {
          sessionId,
          error: { name: boundedErrorName(error), ...sanitizeError(error) },
        });
      });
    },
    [queryClient, sessionId, updateSessionDuration]
  );
}
